import type { ModuleType, PermissionType } from "@/contexts";

/**
 * SEC-GATES-02: what a clinic role can open is decided by the grants the API
 * returns (`roles[0].permissions`, merged across the user's roles by
 * `User::listPermissions`), never by the role name or a module flag alone.
 * Each list names the permissions the matching list endpoint accepts for
 * its own screen (`Controller::authorizeAny`); any one of them is enough.
 */
export type Grant = { module: ModuleType; permission: PermissionType };

export type GrantCheck = (module: ModuleType, permission: PermissionType) => boolean;

export const LIST_GRANTS = {
  patients: [{ module: "patient", permission: "view patient" }],
  schedulings: [{ module: "scheduling", permission: "view scheduling" }],
  financeAccounts: [
    { module: "finance account", permission: "view finance account" },
    { module: "finance entry", permission: "view finance entry" },
    { module: "report", permission: "view cash flow" },
  ],
  budgets: [{ module: "budget", permission: "view budgets" }],
  vouchers: [{ module: "voucher", permission: "view vouchers" }],
  categories: [{ module: "category", permission: "view categories" }],
  providers: [{ module: "providers", permission: "view providers" }],
  procedures: [{ module: "procedure", permission: "view procedure" }],
  employees: [{ module: "employee", permission: "view employee" }],
} as const satisfies Record<string, readonly Grant[]>;

export type ListKey = keyof typeof LIST_GRANTS;

export function canOpenList(check: GrantCheck, list: ListKey): boolean {
  return LIST_GRANTS[list].some((grant) => check(grant.module, grant.permission));
}

/**
 * The accountant does not work conversations. The Inbox read routes carry no
 * permission yet (an api row is a candidate), so the role name decides the
 * menu entry; a user who is also `admin` keeps it.
 */
export function canSeeInbox(roleNames: string[]): boolean {
  return !roleNames.includes("accountant") || roleNames.includes("admin");
}

/**
 * 403 bodies that are not a role refusal: the clinic access, the plan and the
 * pilot read-only mode have their own handling (interceptor, banners). The
 * API puts the code in `code` or in `error` depending on the middleware.
 */
const NON_PERMISSION_403_CODES = new Set([
  "tenant_access_denied",
  "feature_not_in_plan",
  "Feature_not_available",
  "pilot_read_only",
]);

/** True when a request failed because the API refused the user's role (HTTP 403). */
export function isForbiddenError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: unknown; error?: unknown } } } | null)?.response;
  if (response?.status !== 403) return false;
  const { code, error: errorCode } = response.data ?? {};
  return !NON_PERMISSION_403_CODES.has(String(code)) && !NON_PERMISSION_403_CODES.has(String(errorCode));
}
